import { Offer } from 'common/contracts';
import { DEFAULT_OFFERS_BACKGROUND_URL } from 'constants/constants';
import { OfferType } from 'constants/enums';
import { timeUtils } from 'utils/scheduleUtils';

import {
  buildBaseInitialValues,
  buildDynamicOfferUi,
  buildEmblemBadgeInitialValues,
  buildOfferUiIdInitialValue,
  buildPriceDiscount,
  buildPriceFromCents,
  buildProductSale,
  buildProgressBarInitialValues,
  buildSaleDiscountInitialValues,
  buildScheduleInitialValue,
  buildSectionTabInitialValues,
  buildSetAsFreeInitialValue
} from 'views/Offers/Offer.utils';

import type {
  BuildCurrentPromotionParams,
  CurrentPromotion,
  FormValues,
  OfferData,
  OfferUi,
  OfferUIData,
  PlayerAvailability,
  PreviewProduct,
  ProductSequence
} from './PromotionsForm.types';

const buildOfferUi = (
  values: FormValues,
  getOfferUI: OfferUIData
): OfferUi => ({
  backgroundImage:
    getOfferUI?.backgroundImage ||
    values.backgroundImage ||
    DEFAULT_OFFERS_BACKGROUND_URL,
  offerUiType: OfferType.SPECIAL_OFFER,
  specialOffer: {
    templateType: getOfferUI?.specialOffer?.templateType || 'Single',
    presentOfferEndTimer:
      getOfferUI?.specialOffer?.presentOfferEndTimer || false,
    title: getOfferUI?.specialOffer?.title || '',
    fontSize: getOfferUI?.specialOffer?.fontSize || 22,
    fontWeight: getOfferUI?.specialOffer?.fontWeight || 'bold',
    fontColor: getOfferUI?.specialOffer?.fontColor || { colorOne: '#87d256' },
    subTitle: {
      text: getOfferUI?.specialOffer?.subTitle?.text || '',
      fontSize: getOfferUI?.specialOffer?.subTitle?.fontSize || 16,
      fontWeight: getOfferUI?.specialOffer?.subTitle?.fontWeight || 'normal',
      fontColor: getOfferUI?.specialOffer?.subTitle?.fontColor || {
        colorOne: '#ffffff'
      }
    },
    backgroundColor: getOfferUI?.specialOffer?.backgroundColor || {
      colorOne: '#323281'
    }
  },
  borderColor: getOfferUI?.borderColor || {
    colorOne: '#ffffff',
    colorTwo: '',
    direction: ''
  },
  borderWidth: getOfferUI?.borderWidth || 2
});

const buildProductsSequence = (
  values: FormValues,
  productsListForPreview: PreviewProduct[],
  transformPriceValue: () => number
): ProductSequence[] => {
  const hasProductSale = (values.salePercentage ?? 0) > 0;
  const hasPreviewProducts =
    hasProductSale && productsListForPreview.length > 0;

  const products = hasPreviewProducts
    ? productsListForPreview.map((product, index) => {
        if (index !== 0) return product;

        const strokedSaleQuantity = String(
          product.quantity ?? product.quantityDisplay ?? ''
        );

        return {
          ...product,
          strokedSaleQuantity
        };
      })
    : productsListForPreview;

  return [
    {
      index: 1,
      products,
      price: {
        currencyCode: 'USD',
        currencySymbol: '$',
        price: values.price || 0,
        symbolPosition: 'Left',
        spacing: false,
        fractionalSeparator: '.',
        milSeparator: ',',
        isCents: true,
        multiplier: 100
      },
      priceInUsdCents: values.price || 0,
      prices: {
        priceInUsdCents: values.price || 0,
        priceDisplay: values.setAsFree ? '$0.00' : `$${transformPriceValue()}`,
        currencyCode: 'USD'
      }
    }
  ];
};

const buildPlayerAvailability = (
  values: FormValues
): PlayerAvailability | undefined =>
  values.playerAvailability
    ? {
        total: values.playerAvailability,
        remaining: values.playerAvailability
      }
    : undefined;

export const buildCurrentPromotion = ({
  values,
  getOfferUI,
  productsListForPreview,
  badges,
  emblemBadges,
  ribbonBadges,
  transformPriceValue
}: BuildCurrentPromotionParams): CurrentPromotion => ({
  offerId: values.offerId || 'preview',
  publisherOfferId: values.publisherOfferId || '',
  offerJwt: '',
  offerType: OfferType.SPECIAL_OFFER,
  offerName: values.name || '',
  displayName: values.displayName || values.name || '',
  offerUi: buildOfferUi(values, getOfferUI),
  dynamicOfferUi: buildDynamicOfferUi(values, emblemBadges, ribbonBadges),
  badges: badges || [],
  productSale: buildProductSale(values),
  priceDiscount: buildPriceDiscount(values),
  productsSequence: buildProductsSequence(
    values,
    productsListForPreview,
    transformPriceValue
  ),
  playerAvailability: buildPlayerAvailability(values),
  schedule: values.schedule,
  offerBadges: {
    emblems: emblemBadges || [],
    ribbons: ribbonBadges || []
  }
});

export const buildOfferData = (
  currentPromotion: CurrentPromotion
): OfferData => ({
  offers: {
    progressBars: [],
    popups: [],
    promotions: [currentPromotion],
    bundles: [],
    rolling: []
  },
  balances: [],
  offerFocus: {},
  sections: [],
  profileFrameImg: '',
  playerLevelImg: '',
  publisherSettings: {
    popUpStartHourUTC: 0
  },
  bannerImage: '',
  playerLevels: [],
  tabs: [],
  logo: ''
});

// --- Form initial values ---

interface IBuildPromotionFormInitialValuesParams {
  data: Partial<Offer>;
  edit: boolean;
  dup: boolean;
  defaultOfferDesignId: string;
  getOfferResult?: Partial<Offer>;
}

export const buildPromotionFormInitialValues = ({
  data,
  edit,
  dup,
  defaultOfferDesignId,
  getOfferResult
}: IBuildPromotionFormInitialValuesParams) => ({
  ...buildBaseInitialValues(data, edit, dup),
  setAsFree: buildSetAsFreeInitialValue(data, edit, dup),
  offerId: data.offerId,
  price: buildPriceFromCents(data.productsSequence?.[0]?.priceInUsdCents),
  playerAvailability:
    data.productsSequence?.[0]?.playerAvailability ?? undefined,
  type: OfferType.SPECIAL_OFFER,
  offerUiId: buildOfferUiIdInitialValue(
    data, edit, dup, defaultOfferDesignId, getOfferResult
  ),
  coolDownInHours: data.coolDownInHours || 0,
  schedule: buildScheduleInitialValue(data),
  startHour: timeUtils.getTimeFromCron(data?.schedule?.interval || '')
    ?.hourFromCron,
  active: data.active ?? true,
  quantity: '',
  backgroundImage: data?.offerUi?.backgroundImage || '',
  productsSequence: data.productsSequence || {},
  ...buildEmblemBadgeInitialValues(data),
  ...buildSaleDiscountInitialValues(data),
  priceBeforeDiscount: data?.priceDiscount?.priceBeforeDiscount
    ? data.priceDiscount.priceBeforeDiscount / 100
    : null,
  ...buildSectionTabInitialValues(data),
  ...buildProgressBarInitialValues(data)
});
